import type { Fraktion } from "@/components/atomic-crm/misc/fraktionen";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

import type {
  CheckInResult,
  CheckInSession,
  OwnEntry,
  RpcError,
} from "./checkInState";

export type CheckInMe = {
  salesId: number;
  firstName: string;
  fullName: string;
  fraktion: Fraktion | null;
  functions: { district: Fraktion; function: string }[];
};

export type CheckInLoad =
  | { signedIn: false }
  | { signedIn: true; me: CheckInMe | null; entries: OwnEntry[] };

const SESSION_COLUMNS =
  "id, title, starts_at, ends_at, location, cancelled, district, billable";
const RANGE_MS = 36 * 60 * 60 * 1000;

/** Own person and own entries around today; RLS limits both to the signed-in user. */
export const loadCheckIn = async (now: Date): Promise<CheckInLoad> => {
  const sb = getSupabaseClient();
  const {
    data: { session },
  } = await sb.auth.getSession();
  if (!session) return { signedIn: false };

  const { data: sale, error: saleError } = await sb
    .from("sales")
    .select("id, first_name, last_name, fraktion")
    .eq("user_id", session.user.id)
    .maybeSingle();
  if (saleError) throw saleError;
  if (!sale) return { signedIn: true, me: null, entries: [] };

  const [entries, members] = await Promise.all([
    sb
      .from("attendance_entries")
      .select(
        `status, checked_in_at, attendance_sessions!inner(${SESSION_COLUMNS})`,
      )
      .eq("sales_id", sale.id)
      .gte(
        "attendance_sessions.starts_at",
        new Date(now.getTime() - RANGE_MS).toISOString(),
      )
      .lt(
        "attendance_sessions.starts_at",
        new Date(now.getTime() + RANGE_MS).toISOString(),
      ),
    sb
      .from("attendance_members")
      .select("district, function")
      .eq("sales_id", sale.id)
      .eq("active", true),
  ]);
  if (entries.error) throw entries.error;
  if (members.error) throw members.error;

  return {
    signedIn: true,
    me: {
      salesId: sale.id,
      firstName: sale.first_name ?? "",
      fullName: [sale.first_name, sale.last_name].filter(Boolean).join(" "),
      fraktion: sale.fraktion ?? null,
      functions: members.data ?? [],
    },
    entries: (entries.data ?? []).map((row) => ({
      status: row.status,
      checked_in_at: row.checked_in_at,
      session: row.attendance_sessions as unknown as CheckInSession,
    })),
  };
};

export const checkIn = async (
  code: string,
): Promise<{ data: CheckInResult } | { error: RpcError }> => {
  const { data, error } = await getSupabaseClient().rpc("attendance_check_in", {
    code,
  });
  return error ? { error } : { data: data as CheckInResult };
};

export const requestLate = async (
  sessionId: number,
): Promise<{ error: RpcError | null }> => {
  const { error } = await getSupabaseClient().rpc("attendance_request_late", {
    session_id: sessionId,
  });
  return { error };
};
